import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { useTheme, useAuth } from '../App';
import { 
  Sun, Moon, Sparkles, CircleDot, Hexagon, Triangle, 
  Download, Palette, Layers, Wand2, ArrowRight 
} from 'lucide-react';

const Landing = () => {
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <Wand2 className="w-8 h-8" aria-hidden="true" />,
      title: "Intention-to-Form",
      description: "Transform your spiritual intentions into sacred geometry with AI-guided generation."
    },
    {
      icon: <Hexagon className="w-8 h-8" aria-hidden="true" />,
      title: "Sacred Frameworks",
      description: "Choose from Seed of Life, Flower of Life, Golden Ratio spirals, and Metatron's Cube."
    },
    {
      icon: <Palette className="w-8 h-8" aria-hidden="true" />,
      title: "Style Traditions",
      description: "Vedic, Hermetic, Minimalist, Art Nouveau, or Sci-Fi Mystic aesthetics."
    },
    {
      icon: <Layers className="w-8 h-8" aria-hidden="true" />,
      title: "Complete Art Sets",
      description: "Get main artwork, social crops, poster layouts, and meaning cards with ritual prompts."
    },
    {
      icon: <Download className="w-8 h-8" aria-hidden="true" />,
      title: "Print-Ready Exports",
      description: "High-res PNG, SVG for laser cutting, and PDF poster layouts."
    },
    {
      icon: <Sparkles className="w-8 h-8" aria-hidden="true" />,
      title: "Energy Presets",
      description: "Save your favorite combinations and generate consistent series."
    }
  ];

  return (
    <>
      <Helmet>
        <title>MandalaMuse | AI-Powered Sacred Geometry Studio - Create Mandalas & Yantras</title>
        <meta name="description" content="Transform your spiritual intentions into ritual-ready mandalas, yantras, and sigils. AI-powered sacred geometry art generator with print-ready exports. Start free with 10 credits." />
        <link rel="canonical" href="https://mandalamuse.com/" />
      </Helmet>
      
      <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }} />
      </div>

      {/* Navigation */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 z-50" role="navigation" aria-label="Main navigation">
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="glass rounded-full px-6 py-3 flex items-center gap-6"
        >
          <Link to="/" className="flex items-center gap-2" aria-label="MandalaMuse Home">
            <CircleDot className="w-6 h-6 text-primary" aria-hidden="true" />
            <span className="font-heading text-xl font-medium">MandalaMuse</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#why-different" className="hover:text-foreground transition-colors">Why Us</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" aria-hidden="true" /> : <Moon className="w-5 h-5" aria-hidden="true" />}
            </Button>
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button data-testid="go-to-dashboard-btn" className="rounded-full px-6">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button data-testid="get-started-btn" className="rounded-full px-6">
                  Get Started
                </Button>
              </Link>
            )}
          </div>
        </motion.div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 flex flex-col items-center justify-center min-h-screen" aria-labelledby="hero-heading">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative mb-12"
          aria-hidden="true"
        >
          {/* Decorative mandala outline */}
          <div className="w-64 h-64 md:w-80 md:h-80 relative">
            <svg viewBox="0 0 200 200" className="w-full h-full animate-spin-slow opacity-20">
              <circle cx="100" cy="100" r="95" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-primary" />
              <circle cx="100" cy="100" r="80" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-accent" />
              <circle cx="100" cy="100" r="60" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-primary" />
              {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                <circle
                  key={i}
                  cx={100 + 40 * Math.cos((angle * Math.PI) / 180)}
                  cy={100 + 40 * Math.sin((angle * Math.PI) / 180)}
                  r="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-accent"
                />
              ))}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-2xl" />
            </div>
          </div>
        </motion.div>

        <motion.h1
          id="hero-heading"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-heading text-5xl md:text-7xl lg:text-8xl text-center max-w-4xl leading-tight"
        >
          Sacred Geometry <br />
          <span className="text-gradient">Meets Intention</span>
        </motion.h1>

        <motion.p
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-8 text-lg md:text-xl text-muted-foreground text-center max-w-2xl font-light"
        >
          Transform your spiritual intentions into ritual-ready mandalas, yantras, and sigils. 
          AI-powered sacred geometry for modern mystics.
        </motion.p>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <Link to={isAuthenticated ? "/studio" : "/register"}>
            <Button 
              size="lg" 
              data-testid="enter-studio-btn"
              className="rounded-full px-10 py-6 text-base font-semibold tracking-wide uppercase glow-primary"
            >
              Enter the Studio
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <a href="#features">
            <Button 
              variant="outline" 
              size="lg"
              data-testid="explore-features-btn"
              className="rounded-full px-10 py-6 text-base font-medium tracking-wide"
            >
              Explore Features
            </Button>
          </a>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-6 relative" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto">
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 id="features-heading" className="font-heading text-4xl md:text-5xl mb-6">The Art of Creation</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Everything you need to create meaningful, consistent sacred geometry art for your spiritual practice or business.
            </p>
          </motion.header>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" role="list">
            {features.map((feature, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 hover:-translate-y-1 transition-all duration-500"
                role="listitem"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 text-primary">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-2xl mb-3">{feature.title}</h3>
                <p className="text-muted-foreground font-light">{feature.description}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* AI vs Procedural Comparison Section */}
      <section id="why-different" className="py-32 px-6 relative overflow-hidden" aria-labelledby="comparison-heading">
        <div className="max-w-7xl mx-auto">
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-semibold uppercase tracking-widest mb-4 block">Why MandalaMuse?</span>
            <h2 id="comparison-heading" className="font-heading text-4xl md:text-5xl mb-6">
              Not Just Another AI Art Generator
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
              Generic AI creates approximations. Our engine executes mathematical precision.
            </p>
          </motion.header>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Comparison */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="glass-card p-4 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d8b6d71e-d5f0-439c-aa18-7645795acd88/artifacts/1u4k6mgk_Gemini_Generated_Image_qdb0kxqdb0kxqdb0.png"
                  alt="Comparison between raw AI sketch with organic imprecise lines versus MandalaMuse refined procedural sigil with mathematical precision"
                  className="w-full rounded-lg"
                  data-testid="ai-comparison-image"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl -z-10" />
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-accent/20 rounded-full blur-2xl -z-10" />
            </motion.div>

            {/* Explanation Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="glass-card p-6 border-l-4 border-red-500/50">
                  <h3 className="font-heading text-xl mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Raw AI Generation
                  </h3>
                  <ul className="text-muted-foreground space-y-2 text-sm">
                    <li>• Organic, wobbly lines that look "sketched"</li>
                    <li>• Inconsistent stroke weights</li>
                    <li>• Imprecise intersections and tangencies</li>
                    <li>• Hallucinated geometry that doesn't follow sacred ratios</li>
                    <li>• Low contrast, muddy when scaled down</li>
                  </ul>
                </div>

                <div className="glass-card p-6 border-l-4 border-primary">
                  <h3 className="font-heading text-xl mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary" />
                    MandalaMuse Procedural Engine
                  </h3>
                  <ul className="text-muted-foreground space-y-2 text-sm">
                    <li>• Mathematically calculated coordinates (φ = 1.618...)</li>
                    <li>• Perfect tangency and vertex alignment</li>
                    <li>• 3:1 stroke hierarchy for visual clarity</li>
                    <li>• Boolean path operations for crisp duality</li>
                    <li>• Logo-grade output ready for laser cutting</li>
                  </ul>
                </div>
              </div>

              <div className="p-6 bg-primary/5 rounded-xl border border-primary/20">
                <p className="text-sm text-muted-foreground italic">
                  "We've trained our geometry engine with thousands of error corrections and sacred geometry rules. 
                  The result isn't AI guessing what geometry looks like—it's a precision architect executing coordinate-based construction."
                </p>
              </div>

              <Link to={isAuthenticated ? "/studio" : "/register"}>
                <Button 
                  size="lg" 
                  data-testid="try-procedural-btn"
                  className="rounded-full px-8 gap-2"
                >
                  Try Procedural Mode
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 px-6 relative" aria-labelledby="pricing-heading">
        <div className="max-w-5xl mx-auto">
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 id="pricing-heading" className="font-heading text-4xl md:text-5xl mb-6">Simple Credit System</h2>
            <p className="text-muted-foreground text-lg">
              Start free with 10 credits. Buy more as you need.
            </p>
          </motion.header>

          <div className="grid md:grid-cols-3 gap-8" role="list" aria-label="Pricing plans">
            {[
              { name: "Starter", credits: 10, price: 5, popular: false },
              { name: "Creator", credits: 30, price: 12, popular: true },
              { name: "Unlimited", credits: 100, price: 35, popular: false },
            ].map((plan, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card p-8 relative ${plan.popular ? 'border-primary/50' : ''}`}
                role="listitem"
                aria-label={`${plan.name} plan - $${plan.price} for ${plan.credits} credits`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full uppercase tracking-wide">
                    Popular
                  </div>
                )}
                <h3 className="font-heading text-2xl mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-heading">${plan.price}</span>
                  <span className="text-muted-foreground">/ {plan.credits} credits</span>
                </div>
                <ul className="space-y-3 mb-8 text-muted-foreground" aria-label={`${plan.name} features`}>
                  <li className="flex items-center gap-2">
                    <Triangle className="w-4 h-4 text-primary fill-primary" aria-hidden="true" />
                    {plan.credits} artwork generations
                  </li>
                  <li className="flex items-center gap-2">
                    <Triangle className="w-4 h-4 text-primary fill-primary" aria-hidden="true" />
                    High-res exports (PNG, SVG, PDF)
                  </li>
                  <li className="flex items-center gap-2">
                    <Triangle className="w-4 h-4 text-primary fill-primary" aria-hidden="true" />
                    Meaning cards & ritual prompts
                  </li>
                </ul>
                <Link to={isAuthenticated ? "/credits" : "/register"}>
                  <Button 
                    className={`w-full rounded-full ${plan.popular ? '' : 'variant-outline'}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  >
                    Get Started
                  </Button>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto glass-card p-12 md:p-16 text-center"
        >
          <h2 className="font-heading text-4xl md:text-5xl mb-6">
            Ready to Create Sacred Art?
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-xl mx-auto">
            Join thousands of spiritual creators using MandalaMuse for their intention-based art.
          </p>
          <Link to={isAuthenticated ? "/studio" : "/register"}>
            <Button 
              size="lg" 
              data-testid="cta-start-btn"
              className="rounded-full px-12 py-6 text-base font-semibold tracking-wide uppercase glow-primary"
            >
              Start Creating Free
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border" role="contentinfo">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <CircleDot className="w-5 h-5 text-primary" aria-hidden="true" />
            <span className="font-heading text-lg">MandalaMuse</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 MandalaMuse. Sacred geometry for modern mystics.
          </p>
        </div>
      </footer>
    </div>
    </>
  );
};

export default Landing;
